import { useState, useEffect, useRef } from 'react';
import useDebounce from '../../../hooks/useDebounce';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Tabs,
  Tab,
  Chip,
  Typography,
} from '@mui/material';
import { Add, Search, TrendingUp, Assignment, CheckCircle, HourglassEmpty, Download, Label } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import CorrespondenceTable from '../components/CorrespondenceTable';
import CorrespondenceModalForm from '../components/CorrespondenceModalForm';
import LoadingLogo from '../../../components/LoadingLogo';
import correspondenceService from '../services/correspondenceService';
import { companyService } from '../../companies';
import { correspondenceTypeService } from '../../correspondence-types';

const CorrespondenceList = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [correspondences, setCorrespondences] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [types, setTypes] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedCorrespondence, setSelectedCorrespondence] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 = Mi area, 1 = Etiquetado
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (user?.companyId && !selectedCompany) {
      setSelectedCompany(user.companyId.toString());
    }
  }, [user?.companyId]);

  useEffect(() => {
    if (selectedCompany) {
      loadTypes(selectedCompany);
    } else {
      setTypes([]);
    }
    setSelectedType('');
  }, [selectedCompany]);

  useEffect(() => {
    loadCorrespondences();
  }, [page, debouncedSearch, selectedCompany, selectedStatus, selectedType, activeTab]);

  useEffect(() => {
    loadStats();
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadTypes = async (companyId) => {
    try {
      const response = await correspondenceTypeService.getAll({ companyId, limit: 100 });
      setTypes(response.data);
    } catch (error) {
      console.error('Error al cargar tipos:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await correspondenceService.getStats({ companyId: selectedCompany });
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadisticas:', error);
    }
  };

  const loadCorrespondences = async () => {
    try {
      setLoading(true);
      const response = await correspondenceService.getAll({
        search: debouncedSearch,
        companyId: selectedCompany,
        status: selectedStatus,
        correspondenceTypeId: selectedType || undefined,
        page,
        limit: 10,
        ...(activeTab === 1 && { tagged: 'true' }),
      });
      setCorrespondences(response.data);
      setPagination(response.pagination);
    } catch (error) {
      showSnackbar('Error al cargar correspondencias', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedCorrespondence(null);
    setOpenModal(true);
  };

  const handleEdit = (correspondence) => {
    setSelectedCorrespondence(correspondence);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estas seguro de eliminar esta correspondencia?')) {
      try {
        await correspondenceService.delete(id);
        setCorrespondences(prev => prev.filter(c => c.id !== id));
        setPagination(prev => prev ? { ...prev, total: (prev.total || 1) - 1 } : prev);
        showSnackbar('Correspondencia eliminada exitosamente', 'success');
      } catch (error) {
        showSnackbar('Error al eliminar correspondencia', 'error');
      }
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedCorrespondence) {
        await correspondenceService.update(selectedCorrespondence.id, data);
        setCorrespondences(prev => prev.map(c =>
          c.id === selectedCorrespondence.id ? { ...c, ...data } : c
        ));
        showSnackbar('Correspondencia actualizada exitosamente', 'success');
      } else {
        await correspondenceService.create(data);
        showSnackbar('Correspondencia creada exitosamente', 'success');
        if (page !== 1) setPage(1);
        else loadCorrespondences();
        loadStats();
      }
      setOpenModal(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar correspondencia', 'error');
      throw error;
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleToggleRead = async (id) => {
    try {
      const response = await correspondenceService.toggleRead(id);
      setCorrespondences(prev => prev.map(c =>
        c.id === id ? { ...c, isRead: response.data.isRead } : c
      ));
    } catch (error) {
      showSnackbar('Error al cambiar estado de lectura', 'error');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Polling: silently refresh every 30 seconds when no modal is open
  const pollingRef = useRef(null);
  useEffect(() => {
    pollingRef.current = setInterval(() => {
      if (!openModal) {
        loadCorrespondences();
      }
    }, 30000);
    return () => clearInterval(pollingRef.current);
  }, [debouncedSearch, selectedCompany, selectedStatus, selectedType, page, openModal, activeTab]);

  return (
    <Box>
      <PageHeader
        title="Correspondencia"
        subtitle="Radicacion de entrada y salida, asignacion e hilos de conversacion."
        stats={[
          { label: 'Total', value: stats.total || 0 },
          { label: 'Registradas', value: stats.registered || 0 },
          { label: 'Asignadas', value: stats.assigned || 0 },
          { label: 'Cerradas', value: stats.closed || 0 },
        ]}
      />

      {/* Actions row: Tabs + Date range + Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Tabs value={activeTab} onChange={(_, v) => { setActiveTab(v); setPage(1); }}>
          <Tab label="Mi area" />
          <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Label fontSize="small" /> Etiquetado</Box>} />
        </Tabs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid rgba(29,31,32,0.16)', px: 1.5, py: 0.5 }}>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(29,31,32,0.55)' }}>
              Rango
            </Typography>
            <TextField type="date" size="small" value={exportStart} onChange={(e) => setExportStart(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ width: 155, '& .MuiOutlinedInput-root': { bgcolor: 'transparent', '& fieldset': { border: 'none' } } }} />
            <Typography sx={{ color: 'rgba(29,31,32,0.4)' }}>&rarr;</Typography>
            <TextField type="date" size="small" value={exportEnd} onChange={(e) => setExportEnd(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ width: 155, '& .MuiOutlinedInput-root': { bgcolor: 'transparent', '& fieldset': { border: 'none' } } }} />
          </Box>
          <Button variant="outlined" startIcon={<Download />} onClick={() => correspondenceService.exportExcel({ startDate: exportStart, endDate: exportEnd, companyId: selectedCompany, search: debouncedSearch, status: selectedStatus })}>
            Exportar Excel
          </Button>
          {hasPermission('correspondence.create') && (
            <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
              Nueva Correspondencia
            </Button>
          )}
        </Box>
      </Box>

      {/* Filtros */}
      <Box sx={{ bgcolor: '#f2f2f3', borderRadius: 0, p: '18px', border: '1px solid rgba(29,31,32,0.16)', mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Buscar</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por titulo, radicado o destinatario..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Empresa</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedCompany}
                onChange={(e) => { setSelectedCompany(e.target.value); setPage(1); }}
                displayEmpty
              >
                <MenuItem value="">Todas</MenuItem>
                {companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Tipo</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedType}
                onChange={(e) => { setSelectedType(e.target.value); setPage(1); }}
                displayEmpty
                disabled={types.length === 0}
              >
                <MenuItem value="">Todos</MenuItem>
                {types.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Estado</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedStatus}
                onChange={(e) => { setSelectedStatus(e.target.value); setPage(1); }}
                displayEmpty
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="registered">Registradas</MenuItem>
                <MenuItem value="assigned">Asignadas</MenuItem>
                <MenuItem value="closed">Cerradas</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingLogo size={120} />
        </Box>
      ) : (
        <CorrespondenceTable
          correspondences={correspondences}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleRead={handleToggleRead}
          onRefresh={loadCorrespondences}
          page={page}
          onPageChange={setPage}
          pagination={pagination}
          canEdit={hasPermission('correspondence.update')}
          canDelete={hasPermission('correspondence.delete')}
        />
      )}

      <CorrespondenceModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        correspondence={selectedCorrespondence}
        companies={companies}
        types={types}
        onCompanyChange={loadTypes}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CorrespondenceList;
